import {
  getEffectiveTrackMatchStatusId,
  isTrackMatchStatusProcessing,
  TRACK_MATCH_STATUS_LATER,
  trackMatchingStatusHidesCandidates,
} from '@/constants/trackMatchStatus';
import type { useTrackMatchingStore } from '@/stores/trackMatchingStore';
import { SOURCE_TYPES, type SourceType } from '@/trackMatching/trackMatchingTypes';

export type TrackMatchingQueueActionType = 'link' | 'unlink' | 'isrc' | 'markdone';

/** Minimal row shape for queue toggles and selected-state checks in the actions column. */
export interface TrackMatchingActionRow {
  live_track_id: string | number;
  source: string;
  id: string | number;
  isrc?: string;
  player?: string;
  [key: string]: unknown;
}

export type TrackMatchingStore = ReturnType<typeof useTrackMatchingStore>;

export function useTrackMatchingQueueActions(store: TrackMatchingStore) {
  function isRowActionSelected(
    row: TrackMatchingActionRow,
    actionType: TrackMatchingQueueActionType
  ): boolean {
    if (actionType === 'markdone') {
      return store.isMarkQueued(row.live_track_id);
    }

    return store.isQueueActionSelected(
      row.live_track_id,
      row.source as SourceType,
      row.id,
      actionType
    );
  }

  function queueRowAction(actionType: TrackMatchingQueueActionType, row: TrackMatchingActionRow): void {
    if (actionType === 'markdone') {
      store.toggleMarkQueued(row.live_track_id);
      return;
    }

    store.toggleQueueAction(row.live_track_id, row.source as SourceType, row.id, actionType);
  }

  function getMarkDoneTooltip(liveTrackId: string | number): string {
    const state = store.getState(liveTrackId, SOURCE_TYPES.LIVE_TRACK, liveTrackId);
    const statusId = getEffectiveTrackMatchStatusId(state.matchData as Record<string, unknown> | undefined);
    if (isTrackMatchStatusProcessing(statusId)) {
      return 'Match updates in progress (Processing)';
    }
    if (state.markQueued) {
      // SAU-175: dropped the NEVER↔LATER toggle; mark queues a single LATER, unmark
      // queues whatever HAS_MATCHES/NO_MATCHES recompute the FE derives from existing matches.
      if (statusId === TRACK_MATCH_STATUS_LATER) {
        return 'Undo queued unmark — re-marks Later on save';
      }
      return 'Undo queued Later (not saved yet)';
    }
    if (trackMatchingStatusHidesCandidates(statusId) && !state.reveal_done_matches) {
      return 'Candidates hidden — click to show match rows';
    }
    if (trackMatchingStatusHidesCandidates(statusId) && state.reveal_done_matches) {
      if (statusId === TRACK_MATCH_STATUS_LATER) {
        return 'Clear Later — recompute matches on save';
      }
      return 'Hide candidate matches again';
    }
    return 'Mark Later (hide candidates)';
  }

  function isRevealDoneMatches(row: TrackMatchingActionRow): boolean {
    if (row.source !== SOURCE_TYPES.LIVE_TRACK) {
      return false;
    }
    return (
      store.getState(row.live_track_id, SOURCE_TYPES.LIVE_TRACK, row.live_track_id).reveal_done_matches ===
      true
    );
  }

  return {
    isRowActionSelected,
    queueRowAction,
    getMarkDoneTooltip,
    isRevealDoneMatches,
  };
}
